import { useCallback, useState } from "react";
import type { OccurrenceVariant } from "../components/KksOccurrenceRow";
import {
  OCCURRENCE_WIDTHS_KEY,
  useUserPreferences,
} from "./useUserPreferences";

const MIN_OCCURRENCE_COL_WIDTH = 40;

/**
 * Column widths for an occurrence table (Anomalies or Search tab).
 * The Anomalies and Search tables have different column sets, so each
 * persists its own widths under a distinct preference key.
 * Widths are resized live in local state and persisted to the user's
 * preferences once the drag ends.
 */
export function useOccurrenceColumnWidths(variant: OccurrenceVariant) {
  const { prefs, update } = useUserPreferences();
  const prefKey = OCCURRENCE_WIDTHS_KEY[variant];
  const [colWidths, setColWidths] = useState(prefs[prefKey]);

  const resizeColumn = useCallback((index: number, delta: number) => {
    setColWidths((prev) => {
      const next = [...prev];
      next[index] = Math.max(MIN_OCCURRENCE_COL_WIDTH, next[index] + delta);
      return next;
    });
  }, []);

  const commitColumnWidths = useCallback(() => {
    setColWidths((widths) => {
      update({ [prefKey]: widths });
      return widths;
    });
  }, [update, prefKey]);

  return { colWidths, resizeColumn, commitColumnWidths };
}
